import { create } from 'zustand'
import type { Edge, Node } from '@xyflow/react'

/**
 * SINGLE shared undo/redo history for the backtest editor.
 *
 * Previously `useUndoRedo` kept its history in per-instance `useState`, so every
 * component that called it (editor page, but also each group/sticky/subnet/
 * slice/expose-params node) got its OWN stack and its OWN Cmd+Z listener. Node-
 * level snapshots landed in stacks the editor's undo never read, and one Cmd+Z
 * fanned out to every mounted instance. This store is the one place history
 * lives, so a snapshot taken anywhere is undoable everywhere.
 *
 * Snapshots are whole-graph but CHEAP: `[...getNodes()]` copies the reference
 * array, not the node data. React Flow updates immutably, so an old entry keeps
 * pointing at the pre-edit object versions (structural sharing) — tens of KB for
 * the full 50-entry history.
 *
 * The store can't call React hooks, so the host registers accessors via
 * `configure`. The editor page registers its REAL graph state as primary:
 * React Flow's own `getNodes()` returns what the canvas displays, and inside a
 * subnet that is a projection with everything outside the subnet hidden. A
 * snapshot of it, restored after leaving the subnet, left the rest of the
 * strategy hidden (live check, 2026-10-06). Node components still call
 * `useUndoRedo()` for `takeSnapshot`; their React Flow accessors stand in only
 * where no primary is registered (tests, or a host without one).
 */

export interface HistoryItem {
  nodes: Node[]
  edges: Edge[]
}

export interface HistoryAccessors {
  getNodes: () => Node[]
  getEdges: () => Edge[]
  setNodes: (nodes: Node[]) => void
  setEdges: (edges: Edge[]) => void
}

export const DEFAULT_MAX_HISTORY_SIZE = 50

/**
 * Heavy, ephemeral node-data fields stripped from stored snapshots. They're
 * recomputed from the latest run (not load-bearing graph state), so keeping
 * them out of history avoids 50 entries pinning stale per-run debug arrays in
 * memory. Only nodes that actually carry one are cloned; everything else keeps
 * its reference (structural sharing).
 */
const EPHEMERAL_DATA_FIELDS = ['debugValueSnapshot', 'debugValueRulerKeys'] as const

function scrubForHistory(nodes: Node[]): Node[] {
  return nodes.map((node) => {
    const data = node.data as Record<string, unknown> | undefined
    if (!data || !EPHEMERAL_DATA_FIELDS.some((f) => f in data)) return node
    const nextData = { ...data }
    for (const f of EPHEMERAL_DATA_FIELDS) delete nextData[f]
    return { ...node, data: nextData }
  })
}

interface HistoryState {
  past: HistoryItem[]
  future: HistoryItem[]
  maxHistorySize: number
  accessors: HistoryAccessors | null
  /** Whether `accessors` are the host's primary ones (its real graph state). */
  primary: boolean
  /**
   * Register accessors (and optionally the history cap). Idempotent. A primary
   * registration is never replaced by a non-primary one.
   */
  configure: (accessors: HistoryAccessors, maxHistorySize?: number, primary?: boolean) => void
  /** Capture current {nodes, edges} as a restore point. Call BEFORE a mutation. */
  takeSnapshot: () => void
  undo: () => void
  redo: () => void
  /** Clear both stacks (e.g. when loading a fresh strategy). */
  reset: () => void
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  past: [],
  future: [],
  maxHistorySize: DEFAULT_MAX_HISTORY_SIZE,
  accessors: null,
  primary: false,

  configure: (accessors, maxHistorySize, primary = false) =>
    set((s) =>
      s.primary && !primary
        ? { maxHistorySize: maxHistorySize ?? s.maxHistorySize }
        : { accessors, primary, maxHistorySize: maxHistorySize ?? s.maxHistorySize },
    ),

  takeSnapshot: () => {
    const { accessors, past, maxHistorySize } = get()
    if (!accessors) return
    const item: HistoryItem = {
      nodes: scrubForHistory(accessors.getNodes()),
      edges: [...accessors.getEdges()],
    }
    // Cap the stack: keep the newest maxHistorySize-1 entries + this one.
    const trimmed =
      past.length >= maxHistorySize ? past.slice(past.length - maxHistorySize + 1) : past
    set({ past: [...trimmed, item], future: [] })
  },

  undo: () => {
    const { accessors, past } = get()
    if (!accessors || past.length === 0) return
    const last = past[past.length - 1]
    const current: HistoryItem = {
      nodes: [...accessors.getNodes()],
      edges: [...accessors.getEdges()],
    }
    set((s) => ({ past: s.past.slice(0, -1), future: [...s.future, current] }))
    accessors.setNodes(last.nodes)
    accessors.setEdges(last.edges)
  },

  redo: () => {
    const { accessors, future } = get()
    if (!accessors || future.length === 0) return
    const head = future[future.length - 1]
    const current: HistoryItem = {
      nodes: [...accessors.getNodes()],
      edges: [...accessors.getEdges()],
    }
    set((s) => ({ future: s.future.slice(0, -1), past: [...s.past, current] }))
    accessors.setNodes(head.nodes)
    accessors.setEdges(head.edges)
  },

  reset: () => set({ past: [], future: [] }),
}))
